/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Intent:     owner creates a new agenda — short form, focused
 * Hierarchy:  name input leads; person picker optional; Crear stays filled
 * Palette:    agenda tokens (teal / cool gray-green)
 * Depth:      borders + hairlines only
 * Surfaces:   sheet card on mobile; dialog card on desktop
 * Typography: Plex Sans labels
 * Spacing:    8px base
 */
import React, { useEffect, useRef, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';
import { getShellMainPanelWidth } from '../../shared/layout/useContentLayout';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { ticketChrome as ticketUi } from '../../shared/ticket/ticketChrome.styles';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { agenda, agendaFonts } from '../domain/agendaTheme';
import type { AppointmentResourceKind } from '../domain/types';

export type AgendaEmployeeOption = {
  id: string;
  name: string;
};

const KIND_OPTIONS: { id: AppointmentResourceKind; label: string }[] = [
  { id: 'person', label: 'Operario' },
  { id: 'bay', label: 'Lift / box' },
  { id: 'equipment', label: 'Equipo' },
];

type Props = {
  visible: boolean;
  creating: boolean;
  canManage: boolean;
  name: string;
  onNameChange: (value: string) => void;
  employeeId: string | null;
  onEmployeeChange: (id: string | null) => void;
  resourceKind: AppointmentResourceKind;
  onResourceKindChange: (kind: AppointmentResourceKind) => void;
  employees: AgendaEmployeeOption[];
  onCreate: () => void;
  onClose: () => void;
};

export function CreateAgendaModal({
  visible,
  creating,
  canManage,
  name,
  onNameChange,
  employeeId,
  onEmployeeChange,
  resourceKind,
  onResourceKindChange,
  employees,
  onCreate,
  onClose,
}: Props) {
  const { width: winW, height: winH } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const modalBackdropPadding = useModalBackdropPadding();
  const isWide = getShellMainPanelWidth(winW) >= CONTENT_WIDE_BREAKPOINT;
  const nameRef = useRef<TextInput>(null);
  const [employeePickerVisible, setEmployeePickerVisible] = useState(false);

  useEffect(() => {
    if (visible && !isWide) {
      const t = setTimeout(() => nameRef.current?.focus(), 300);
      return () => clearTimeout(t);
    }
  }, [visible, isWide]);

  const handleEmployeeSelect = (emp: AgendaEmployeeOption) => {
    onEmployeeChange(emp.id);
    onNameChange(emp.name);
  };

  const handleClearEmployee = () => {
    onEmployeeChange(null);
    onNameChange('');
  };

  const canSubmit = canManage && name.trim().length > 0 && !creating;

  const formFields = (
    <>
      <Text style={styles.hint}>
        La nueva agenda tomará el horario del local. Si no está configurado, usará Lun–Vie, de 09:00 a 18:00.
      </Text>

      <Text style={styles.fieldLabel}>Tipo</Text>
      <View style={styles.kindRow}>
        {KIND_OPTIONS.map((opt) => {
          const active = resourceKind === opt.id;
          return (
            <TouchableOpacity
              key={opt.id}
              style={[styles.kindChip, active && styles.kindChipActive]}
              onPress={() => onResourceKindChange(opt.id)}
              disabled={creating}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
            >
              <Text style={[styles.kindChipText, active && styles.kindChipTextActive]}>{opt.label}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      <Text style={styles.fieldLabel}>Nombre</Text>
      {employeeId ? (
        <View style={styles.fixedNameBox}>
          <Ionicons name="person-outline" size={15} color={agenda.ink2} />
          <Text style={styles.fixedNameText} numberOfLines={1}>
            {name}
          </Text>
          <TouchableOpacity
            onPress={handleClearEmployee}
            disabled={creating}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel="Quitar persona y escribir nombre manual"
          >
            <Text style={styles.fixedNameClear}>Quitar</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <TextInput
          ref={nameRef}
          style={styles.input}
          value={name}
          onChangeText={onNameChange}
          editable={canManage}
          placeholder="Ej. María, Bahía 1"
          placeholderTextColor={agenda.ink2}
          onSubmitEditing={onCreate}
          returnKeyType="done"
        />
      )}

      {employees.length > 0 ? (
        <View style={styles.employeeBlock}>
          <Text style={styles.fieldLabel}>Persona asignada (opcional)</Text>
          <TouchableOpacity
            style={styles.employeePicker}
            onPress={() => setEmployeePickerVisible(true)}
            disabled={!canManage || creating}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel="Seleccionar persona asignada"
          >
            <View style={styles.employeePickerMain}>
              <Text style={styles.employeePickerValue}>
                {employeeId ? employees.find((employee) => employee.id === employeeId)?.name : 'Agenda libre'}
              </Text>
              <Text style={styles.employeePickerHint}>Podés cambiarlo después</Text>
            </View>
            <Ionicons name="chevron-down" size={18} color={agenda.ink2} />
          </TouchableOpacity>
        </View>
      ) : null}
    </>
  );

  const employeePicker = (
    <MasterFilterSheetModal
      visible={employeePickerVisible}
      title="Persona asignada"
      options={employees}
      selectedId={employeeId}
      allOptionLabel="Agenda libre"
      showAllOption
      appearance="ticket"
      onSelect={(id) => {
        if (id === null) handleClearEmployee();
        else {
          const employee = employees.find((item) => item.id === id);
          if (employee) handleEmployeeSelect(employee);
        }
        setEmployeePickerVisible(false);
      }}
      onClose={() => setEmployeePickerVisible(false)}
    />
  );

  const actionsRow = (
    <View style={[styles.actions, { paddingBottom: Math.max(insets.bottom, spacing.sm) }]}>
      <TouchableOpacity
        style={styles.cancelBtn}
        onPress={onClose}
        disabled={creating}
        accessibilityRole="button"
        accessibilityLabel="Cancelar"
      >
        <Text style={styles.cancelBtnText}>Cancelar</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.createBtn, !canSubmit && styles.btnDisabled]}
        onPress={onCreate}
        disabled={!canSubmit}
        accessibilityRole="button"
        accessibilityLabel="Crear agenda"
      >
        <Text style={styles.createBtnText}>
          {creating ? 'Creando…' : 'Crear agenda'}
        </Text>
      </TouchableOpacity>
    </View>
  );

  if (isWide) {
    const modalCardMaxWidth = Math.min(520, winW - 48);
    return (
      <>
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
        <View style={[posUi.modalBackdrop, styles.wideRoot, modalBackdropPadding]}>
          <View
            style={[
              ticketUi.dialogCard,
              styles.dialogCard,
              { maxWidth: modalCardMaxWidth, maxHeight: winH * 0.88, width: '100%' },
            ]}
          >
            <View style={styles.dialogHeader}>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={ticketUi.sheetTitle}>Nueva agenda</Text>
                <Text style={styles.dialogSubtitle}>
                  Creá una agenda por profesional o cabina.
                </Text>
              </View>
              <TouchableOpacity
                onPress={onClose}
                style={ticketUi.sheetCloseBtn}
                activeOpacity={0.6}
                accessibilityRole="button"
                accessibilityLabel="Cerrar"
              >
                <Text style={ticketUi.sheetCloseGlyph}>×</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.dialogBody}>
              {formFields}
            </View>
            {actionsRow}
          </View>
        </View>
      </Modal>
      {employeePicker}
      </>
    );
  }

  return (
    <>
      <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={ticketUi.sheetBackdrop}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        <View
          style={[
            ticketUi.sheetCard,
            styles.sheetFlex,
            { paddingBottom: 0 },
          ]}
        >
          <View style={ticketUi.grabber} />
          <View style={ticketUi.sheetHeader}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={ticketUi.sheetTitle}>Nueva agenda</Text>
              <Text style={styles.dialogSubtitle}>
                Creá una agenda por profesional o cabina.
              </Text>
            </View>
            <TouchableOpacity
              onPress={onClose}
              style={ticketUi.sheetCloseBtn}
              activeOpacity={0.6}
              accessibilityRole="button"
              accessibilityLabel="Cerrar"
            >
              <Text style={ticketUi.sheetCloseGlyph}>×</Text>
            </TouchableOpacity>
          </View>
          <ModalAwareScrollView
            style={styles.sheetScroll}
            contentContainerStyle={styles.sheetScrollContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            bottomOffset={spacing.sm}
          >
            {formFields}
          </ModalAwareScrollView>
          {actionsRow}
        </View>
      </View>
    </Modal>
    {employeePicker}
    </>
  );
}

const styles = StyleSheet.create({
  wideRoot: {
    alignItems: 'center',
  },
  dialogCard: {
    maxWidth: 520,
  },
  dialogHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    paddingHorizontal: 18,
    paddingTop: 16,
    paddingBottom: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
  },
  dialogSubtitle: {
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink,
    marginTop: 6,
    fontFamily: ticketFonts.sans,
  },
  dialogBody: {
    paddingHorizontal: 18,
    paddingVertical: 14,
    gap: 10,
  },
  sheetFlex: {
    minHeight: 320,
  },
  sheetScroll: {
    paddingHorizontal: 18,
  },
  sheetScrollContent: {
    paddingTop: 4,
    paddingBottom: 14,
    gap: 10,
  },
  hint: {
    fontSize: 12,
    color: agenda.ink2,
    lineHeight: 17,
    fontFamily: agendaFonts.sans,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: agenda.ink2,
    marginBottom: 6,
    fontFamily: agendaFonts.sans,
  },
  input: {
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: agenda.radius,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: agenda.ink,
    backgroundColor: agenda.surface2,
    fontSize: 15,
    fontFamily: agendaFonts.sans,
  },
  fixedNameBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: agenda.radius,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: agenda.surface2,
    minHeight: 44,
  },
  fixedNameText: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  fixedNameClear: {
    fontSize: 13,
    fontWeight: '600',
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  kindRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 8,
  },
  kindChip: {
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: agenda.surface2,
  },
  kindChipActive: {
    borderColor: agenda.brand,
    backgroundColor: agenda.chooseBg,
  },
  kindChipText: {
    fontSize: 13,
    fontWeight: '600',
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  kindChipTextActive: {
    color: agenda.chooseInk,
  },
  employeeBlock: {
    gap: 6,
    marginTop: 4,
  },
  employeePicker: {
    minHeight: 48,
    paddingHorizontal: 12,
    paddingVertical: 9,
    borderRadius: agenda.radius,
    borderWidth: 1,
    borderColor: agenda.borderControl,
    backgroundColor: agenda.surface,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  employeePickerMain: { flex: 1, minWidth: 0 },
  employeePickerValue: {
    fontSize: 14,
    fontWeight: '600',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  employeePickerHint: {
    fontSize: 12,
    color: agenda.ink3,
    marginTop: 1,
    fontFamily: agendaFonts.sans,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 18,
    paddingTop: 10,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
  },
  cancelBtn: {
    paddingVertical: 12,
    paddingHorizontal: 8,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  createBtn: {
    flex: 1,
    backgroundColor: agenda.brand,
    borderRadius: agenda.radius,
    paddingVertical: 12,
    paddingHorizontal: 14,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
  },
  createBtnText: {
    color: agenda.brandInk,
    fontWeight: '700',
    fontSize: 14,
    fontFamily: agendaFonts.sans,
  },
  btnDisabled: { opacity: 0.5 },
});
